import { useSelector } from 'react-redux';

// RBAC permission layer (frontend-first). Reads the resolved permissions object
// the backend delivers in `auth.user.permissions`:
//   { full: true }                                — owner/admin or no custom role
//   { full: false, modules, actions, hiddenData } — restricted by a custom role
//
// Defensive default: a missing/undefined permissions object (e.g. a login cached
// before this feature) is treated as FULL access — never lock a user out; the
// next getMe refresh delivers the real permissions.

// Build the helper API from a permissions object. Pure — usable outside React.
// `orgHiddenModules` — org-wide "Modullar ko'rinishi" toggle (Sozlamalar → Menyu):
// a module hidden here is hidden for EVERYONE, including owner/admin — it's a
// business config choice ("we don't use this module"), not a role restriction.
const ANALYTICS_SOURCES = ['funnels', 'calls', 'contacts', 'inbox', 'reviews'];

export function makePermissions(perm, orgHiddenModules = [], eduEnabled = false) {
  const full = !perm || perm.full !== false;
  const modules = Array.isArray(perm?.modules) ? perm.modules : [];
  const actions = perm?.actions && typeof perm.actions === 'object' ? perm.actions : {};
  const hiddenData = Array.isArray(perm?.hiddenData) ? perm.hiddenData : [];
  const orgHidden = Array.isArray(orgHiddenModules) ? orgHiddenModules : [];

  return {
    full,
    // Can this sidebar module / submodule be shown? `dashboard` always visible
    // (unless explicitly org-hidden). Org-level hide always wins over role access.
    canSeeModule: (navKey) => {
      if (orgHidden.includes(navKey)) return false;
      // «Обучение» включает администратор платформы (шаблон «Учебный центр»); у остальных организаций раздела нет
      if (navKey === 'education' || navKey === 'education-groups') return eduEnabled && (full || modules.includes(navKey));
      // «Аналитика» — единый раздел поверх данных других модулей: виден, если открыт хотя бы один из них.
      if (navKey === 'analytics') return full || ANALYTICS_SOURCES.some(m => modules.includes(m) && !orgHidden.includes(m)) || modules.includes('analytics');
      return full || navKey === 'dashboard' || modules.includes(navKey);
    },
    // Is an action (view|create|edit|delete) allowed for a module?
    // Default-allow `view` so a visible module is at least readable.
    can: (navKey, action) => {
      if (full) return true;
      const a = actions[navKey];
      if (!a) return action === 'view';
      return !!a[action];
    },
    // Should a sensitive data field be hidden?
    isHidden: (dataKey) => !full && hiddenData.includes(dataKey),
  };
}

// React hook — binds to the current auth user's permissions.
export function usePermissions() {
  const perm = useSelector((s) => s.auth.user?.permissions);
  const orgHiddenModules = useSelector((s) => s.auth.user?.organization?.settings?.hiddenModules);
  const eduEnabled = useSelector((s) => !!s.auth.user?.organization?.education?.enabled);
  return makePermissions(perm, orgHiddenModules, eduEnabled);
}

// Generic "visible to a role list" check — same pattern as backend's funnelVisible
// (funnel/deal-field visibleToRoles). Empty/missing list → visible to everyone
// (default, backward-compatible). owner/admin always see it, regardless of list.
export function isVisibleToRoles(user, rolesList) {
  if (!user) return true;
  if (user.role === 'owner' || user.role === 'admin') return true;
  if (!Array.isArray(rolesList) || rolesList.length === 0) return true;
  return rolesList.map(String).includes(String(user.customRoleId || ''));
}
